import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  CalendarDays,
  Phone,
  Mail,
  XCircle,
  FileText,
  MapPin,
  Linkedin,
  ClipboardList,
  User,
  Pencil
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../../api";
import { Input } from "../../../components/ui/Input";
import CustomSelect from "../../../components/ui/CustomSelect";
import Tooltip from "../../../components/ui/Tooltip";
import { useModal } from "../../../context/ModalContext";
import { useNotification } from "../../../context/NotificationContext";

export default function ProspectInteractions({ prospect }) {
 const { showToast } = useNotification();
 const [showForm, setShowForm] = useState(false);
 const [interactions, setInteractions] = useState([]);
 const [editingInteractionId, setEditingInteractionId] = useState(null);
 const [formData, setFormData] = useState({
    typeInteraction: "",
    canal: "",
    date: "",
    contenu: "",
    prochaineEtape: "",
    dateProchaineInteraction: "",
    important: false,
 });

 useEffect(() => {
 if ((prospect?.id || prospect?._id)) fetchInteractions();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [prospect]);

 const fetchInteractions = async () => {
 try {
 const res = await api.get(`/interactions/${(prospect?.id || prospect?._id)}`);
      const sorted = res.data.sort((a, b) => {
        if (b.important && !a.important) return 1;
        if (a.important && !b.important) return -1;
        return new Date(b.date || b.createdAt).getTime() - new Date(a.date || a.createdAt).getTime();
      });
      setInteractions(sorted);
 } catch (err) {
 console.error("Erreur récupération interactions :", err);
 }
 };

 const handleChange = (e) => {
 const { name, value, type, checked } = e.target;
 setFormData((prev) => ({
 ...prev,
 [name]: type === "checkbox" ? checked : value,
 }));
 };

 const handleSaveInteraction = async () => {
    const { typeInteraction, canal, date, contenu } = formData;
    if (!typeInteraction || !canal || !date || !contenu) {
      showToast("Tous les champs obligatoires doivent être remplis.", "error");
      return;
    }

    try {
      if (editingInteractionId) {
        await api.put(`/interactions/${editingInteractionId}`, {
          ...formData,
        });
        showToast("Interaction modifiée avec succès", "success");
      } else {
        await api.post("/interactions", {
          ...formData,
          prospectId: prospect?.id || prospect?._id,
          referent: prospect?.ownerMembership?.user?.displayName || prospect?.ownerMembershipId || "Non assigné",
          statut: prospect?.status || prospect?.statut,
        });
        showToast("Interaction ajoutée avec succès", "success");
      }

      await fetchInteractions();
      setShowForm(false);
      setEditingInteractionId(null);
      setFormData({
        typeInteraction: "",
        canal: "",
        date: "",
        contenu: "",
        prochaineEtape: "",
        dateProchaineInteraction: "",
        important: false,
      });
    } catch (err) {
      console.error("Erreur ajout/modification interaction :", err);
      showToast("Erreur lors de l'enregistrement", "error");
    }
  };

  const handleEditClick = (item) => {
    setEditingInteractionId(item.id || item._id);
    setFormData({
      typeInteraction: item.typeInteraction || "",
      canal: item.canal || "",
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : "",
      contenu: item.contenu || "",
      prochaineEtape: item.prochaineEtape || "",
      dateProchaineInteraction: item.dateProchaineInteraction ? new Date(item.dateProchaineInteraction).toISOString().split('T')[0] : "",
      important: item.important || false,
    });
    setShowForm(true);
  };

  const handleAddClick = () => {
    setEditingInteractionId(null);
    setFormData({
      typeInteraction: "",
      canal: "",
      date: new Date().toISOString().split('T')[0],
      contenu: "",
      prochaineEtape: "",
      dateProchaineInteraction: "",
      important: false,
    });
    setShowForm(true);
  };

  const { confirm } = useModal();

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({
      title: "Supprimer l'interaction",
      message: "Voulez-vous vraiment supprimer cette interaction ?",
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/interactions/${id}`);
      fetchInteractions();
      showToast("Interaction supprimée", "success");
 } catch (err) {
 console.error("Erreur suppression :", err);
 showToast("Erreur lors de la suppression", "error");
 }
 };

 const _getCurrentStep = () => {
 switch (prospect?.status) {
 case "NEW":
 case "PENDING":
 return 0;
 case "QUALIFIED":
 return 1;
 case "CONVERTED":
 return 2;
 default:
 return 0;
 }
 };

 const getStepIndex = () => {
 switch (prospect?.status) {
 case "NEW":
 case "PENDING":
 return 0;
 case "QUALIFIED":
 return 1;
 case "CONVERTED":
 return 2;
 default:
 return 0;
 }
 };
 
  const getProgressBarColor = () => {
    const step = getStepIndex();
    return step === 2 ? "bg-[var(--hp-success-600)]" : "bg-primary";
  };
  
  const getProgressBarHeight = () => {
    const step = getStepIndex();
    return step === 0 ? "h-[15%]" : step === 1 ? "h-[50%]" : "h-full";
  };
  
  const getInteractionIcon = (type) => {
    switch(type) {
      case "Appel": return <Phone className="w-4 h-4 text-secondary-600" />;
      case "Email": return <Mail className="w-4 h-4 text-warning-500" />;
      case "Rendez-vous": return <CalendarDays className="w-4 h-4 text-success-500" />;
      case "Message LinkedIn": return <Linkedin className="w-4 h-4 text-primary-500" />;
      case "Note interne": return <ClipboardList className="w-4 h-4 text-subtle" />;
      case "Autre": return <User className="w-4 h-4 text-subtle" />;
      default: return <FileText className="w-4 h-4 text-primary-500" />;
    }
  };

 if (!prospect) {
  return (
  <div className="workspace-card bg-surface shadow-hp-sm rounded-xl p-6 h-[300px] flex-[2] xl:flex-[3] flex flex-col items-center justify-center text-center space-y-4 relative overflow-hidden">
  <img src={'/assets/logo-hipparchus.png'} alt="Logo Nanthiat" className="w-20 h-20" />
 <p className="text-subtle">Sélectionnez un prospect pour voir son historique d’interactions.</p>
 </div>
 );
 }

  return (
  <div className="workspace-card bg-surface shadow-hp-sm rounded-xl p-6 h-[300px] flex-[2] xl:flex-[3] flex flex-row justify-between relative overflow-hidden gap-4">
  {/* 📍 Gauche : Liste des interactions */}
  <div className="flex-1 flex flex-col h-full border-r border-workspace pr-4">
 <div className="flex items-center justify-between mb-4">
 <h2 className="text-lg font-semibold text-primary">Dernières interactions</h2>
 <button
 onClick={handleAddClick}
 className="btn btn-primary"
 >
 <Plus className="w-5 h-5 mr-1" />
 Ajouter
 </button>
 </div>

  {interactions.length > 0 ? (
    <div className="pr-3 flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-workspace scrollbar-track-transparent mt-2 pl-4 pb-2">
      <div className="space-y-6 relative border-l-2 border-workspace mr-2">
        {interactions.map((item) => (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            key={item.id || item._id}
            className="group relative pl-6 pr-10 z-10 hover:z-40"
          >
            <div className="absolute -left-[11px] top-0.5 bg-surface p-0.5 rounded-full border border-workspace shadow-sm">
              {getInteractionIcon(item.typeInteraction)}
            </div>
            
            <div className="flex justify-between items-start mb-1">
              <p className="text-sm text-primary font-bold flex items-center gap-2">
                {item.typeInteraction}
                <span className="text-xs font-medium text-subtle px-2 py-0.5 bg-surface-muted rounded border border-workspace">
                  {item.canal}
                </span>
                {item.important && (
                  <Tooltip content="Important">
                    <AlertTriangle className="w-4 h-4 text-warning-500" />
                  </Tooltip>
                )}
              </p>
              <div className="flex items-center gap-3 text-xs text-subtle">
                {new Date(item.date).toLocaleDateString()}
              </div>
            </div>
            
            <p className="text-sm text-muted">{item.contenu}</p>

            {item.prochaineEtape && (
              <div className="mt-2 text-xs text-secondary-700 bg-secondary-50/50 p-2 rounded border border-secondary-100 flex items-start gap-2">
                <span className="shrink-0 mt-0.5"><MapPin className="w-3.5 h-3.5 text-secondary-600" /></span>
                <p>
                  <span className="font-semibold">Prochaine étape:</span> {item.prochaineEtape}
                  {item.dateProchaineInteraction && ` (le ${new Date(item.dateProchaineInteraction).toLocaleDateString()})`}
                </p>
              </div>
            )}

            <div className="absolute bottom-0 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity translate-y-1/2">
              <Tooltip content="Modifier" position="top">
                <button
                  onClick={() => handleEditClick(item)}
                  className="text-primary-600 hover:text-primary-800 transition-colors p-1 bg-surface rounded-full shadow-sm border border-workspace"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
              <Tooltip content="Supprimer" position="top">
                <button
                  onClick={() => handleDelete(item.id || item._id)}
                  className="text-danger-600 hover:text-danger-800 transition-colors p-1 bg-surface rounded-full shadow-sm border border-workspace"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center py-6 text-subtle">
      <FileText className="w-8 h-8 mb-2 opacity-20" />
      <p className="text-sm">Aucune interaction récente</p>
    </div>
  )}
  </div>

  {/* 📍 Droite : Progress bar */}
  <div className="w-24 flex-none flex flex-col items-start justify-between py-2 pl-4">
    <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted mb-4">
      Onboarding
    </h3>

    <div className="relative flex flex-col justify-between items-start h-full w-full">
      {/* Barre de fond */}
      <div className="absolute left-3 top-1 bottom-1 w-0.5 bg-surface-muted rounded-full z-0 transform -translate-x-1/2" />

      {/* Barre de progression */}
      <div
        className={`absolute top-1 left-3 w-0.5 transition-all duration-700 ease-in-out rounded-full z-10 shadow-sm ${getProgressBarColor()} ${getProgressBarHeight()} transform -translate-x-1/2`}
      />

      {/* Étapes */}
      {["Ajout", "Mission", "Audit"].map(
        (label, idx) => {
          const isCompleted = getStepIndex() > idx;
          const isCurrent = getStepIndex() === idx;

          return (
            <div key={idx} className="z-20 flex flex-row items-center relative w-full justify-start">
              <motion.div
                initial={false}
                animate={{
                  scale: isCurrent ? 1.15 : 1,
                  backgroundColor: isCompleted ? "var(--hp-success-600)" : isCurrent ? "var(--hp-color-primary)" : "var(--hp-color-surface)",
                  borderColor: isCompleted ? "var(--hp-success-600)" : isCurrent ? "var(--hp-color-primary)" : "var(--hp-color-border)",
                  boxShadow: isCurrent ? "0 0 0 4px var(--hp-color-selected)" : "0 0 0 0 transparent"
                }}
                className={`w-6 h-6 flex items-center justify-center rounded-full border shadow-sm transition-all duration-300 relative z-20`}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-[var(--hp-color-surface)]" />
                ) : (
                  <span className={`text-[10px] font-bold ${isCurrent ? "text-[var(--hp-color-surface)]" : "text-subtle"}`}>{idx + 1}</span>
                )}
              </motion.div>
              <span className={`absolute left-8 text-[11px] font-medium tracking-wide transition-colors duration-300 ${isCurrent ? 'text-primary font-bold' : 'text-muted'}`}>
                {label}
              </span>
            </div>
          );
        }
      )}
    </div>
  </div>

  {/* Modal personnalisé */}
  <AnimatePresence>
  {showForm && (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[rgb(var(--hp-ink-950-rgb)/0.5)] backdrop-blur-sm flex items-center justify-center z-[70] p-4"
      onClick={() => setShowForm(false)}
    >
      <motion.div 
        initial={{ scale: 0.95, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 10, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="bg-surface rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 workspace-icon-button">
          <XCircle className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-semibold text-primary">
          {editingInteractionId ? "Modifier l'interaction" : "Nouvelle interaction"}
        </h3>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Type d'interaction *</label>
            <CustomSelect
              value={formData.typeInteraction}
              onChange={(val) => handleChange({ target: { name: 'typeInteraction', value: val } })}
              placeholder="-- Sélectionner --"
              options={[
                { label: "Appel", value: "Appel" },
                { label: "Email", value: "Email" },
                { label: "Rendez-vous", value: "Rendez-vous" },
                { label: "Message LinkedIn", value: "Message LinkedIn" },
                { label: "Note interne", value: "Note interne" },
                { label: "Autre", value: "Autre" }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Canal *</label>
            <CustomSelect
              value={formData.canal}
              onChange={(val) => handleChange({ target: { name: 'canal', value: val } })}
              placeholder="-- Sélectionner --"
              options={[
                { label: "Téléphone", value: "Téléphone" },
                { label: "Email", value: "Email" },
                { label: "LinkedIn", value: "LinkedIn" },
                { label: "Visio", value: "Visio" },
                { label: "WhatsApp", value: "WhatsApp" },
                { label: "Autre", value: "Autre" }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Date *</label>
            <Input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Contenu *</label>
            <textarea
              name="contenu"
              value={formData.contenu}
              onChange={handleChange}
              rows={3}
              className="workspace-input mt-1 w-full resize-none"
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Prochaine étape</label>
            <Input
              name="prochaineEtape"
              value={formData.prochaineEtape}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Date prochaine interaction</label>
            <Input
              type="date"
              name="dateProchaineInteraction"
              value={formData.dateProchaineInteraction}
              onChange={handleChange}
            />
          </div>

          <div className="flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              name="important"
              checked={formData.important}
              onChange={handleChange}
              className="h-4 w-4 text-primary rounded border-workspace focus:ring-primary-500"
            />
            <label className="text-sm text-primary">Marquer comme important</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-workspace">
          <button
            className="btn btn-secondary"
            onClick={() => setShowForm(false)}
          >
            Annuler
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveInteraction}
          >
            Enregistrer
          </button>
        </div>
      </motion.div>
    </motion.div>
  )}
  </AnimatePresence>
 </div>
 );
}
